<template>
  <div class="identity-card">
    <!-- Photo Section -->
    <div class="photo-section">
      <div class="photo-container">
        <img 
          v-if="usagerInfo.photo" 
          :src="usagerInfo.photo" 
          :alt="`Photo de ${usagerInfo.nom} ${usagerInfo.prenom}`"
          class="user-photo"
        >
        <div v-else class="no-photo">
          <i class="fas fa-user"></i>
        </div>
      </div>
    </div>

    <!-- Info Section -->
    <div class="info-section">
      <div class="card-header">
        <h2>{{ usagerInfo.nom }} {{ usagerInfo.prenom }}</h2>
        <div class="status-badge" :class="usagerInfo.etatUsager === 'Non Existant' ? 'non-existent' : 'existant'">
          {{ usagerInfo.etatUsager }}
        </div>
      </div>

      <div class="info-grid">
        <div class="info-item">
          <label>NPI :</label>
          <span>{{ usagerInfo.npi || 'Non renseigné' }}</span>
        </div>
        
        <div class="info-item">
          <label>Matricule :</label>
          <span>{{ usagerInfo.matricule || 'Non renseigné' }}</span>
        </div>
        
        <div class="info-item">
          <label>Téléphone :</label>
          <span>{{ usagerInfo.tel || 'Non renseigné' }}</span>
        </div>
        
        <div class="info-item">
          <label>Email :</label>
          <span>{{ usagerInfo.email || 'Non renseigné' }}</span>
        </div>
        
        <div class="info-item">
          <label>Date de naissance :</label>
          <span>{{ formatDate(usagerInfo.date_naissance) }}</span>
        </div>
        
        <div class="info-item">
          <label>IFU :</label>
          <span>{{ usagerInfo.ifu || 'Non renseigné' }}</span>
        </div>
        
        <div class="info-item">
          <label>Nationalité :</label>
          <span>{{ usagerInfo.nationalite || 'Non renseigné' }}</span>
        </div>
        
        <div class="info-item full-width">
          <label>Adresse :</label>
          <span>{{ usagerInfo.adresse || 'Non renseigné' }}</span>
        </div>
      </div>

      <!-- Emplacements Section -->
      <div v-if="usagerInfo.emplacements && usagerInfo.emplacements.length > 0" class="emplacements-section">
        <h4>Emplacements :</h4>
        <div class="emplacements-grid">
          <div 
            v-for="emplacement in usagerInfo.emplacements" 
            :key="emplacement.codeemp"
            class="emplacement-item"
          >
            <span class="emplacement-code">{{ emplacement.codeemp }}</span>
            <span class="emplacement-type">{{ emplacement.type || 'Non spécifié' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

interface UsagerInfo {
  nom: string
  prenom: string
  npi?: string
  matricule?: string
  tel?: string
  email?: string
  date_naissance?: string
  ifu?: string
  nationalite?: string
  adresse?: string
  photo?: string
  etatUsager?: string
  emplacements?: Array<{
    codeemp: string
    type?: string
  }>
}

export default defineComponent({
  name: 'IdentityCard',
  props: {
    usagerInfo: {
      type: Object as () => any,
      required: true
    }
  },
  methods: {
    formatDate(date: any) {
      if (!date) return 'Non renseigné'
      return new Date(date).toLocaleDateString('fr-FR')
    }
  }
})
</script>

<style scoped>
.identity-card {
  display: flex;
  gap: 30px;
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  border: 1px solid #e9ecef;
}

.photo-section {
  flex-shrink: 0;
}

.photo-container {
  width: 150px;
  height: 150px;
  border-radius: 15px;
  overflow: hidden;
  border: 3px solid #007bff;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 15px rgba(0,123,255,0.2);
}

.user-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-photo {
  font-size: 4rem;
  color: #6c757d;
}

.info-section {
  flex: 1;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f8f9fa;
}

.card-header h2 {
  margin: 0;
  color: #333;
  font-size: 1.8rem;
  font-weight: 600;
}

.status-badge {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: bold;
  text-transform: uppercase;
}

.status-badge.existant {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-badge.non-existent {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 25px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.info-item span {
  color: #333;
  font-size: 1rem;
  padding: 5px 0;
}

.emplacements-section {
  margin-top: 25px;
  padding-top: 20px;
  border-top: 2px solid #f8f9fa;
}

.emplacements-section h4 {
  margin-bottom: 15px;
  color: #333;
  font-size: 1.1rem;
}

.emplacements-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.emplacement-item {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #dee2e6;
  transition: all 0.3s;
}

.emplacement-item:hover {
  background: #e9ecef;
  transform: translateY(-2px);
  box-shadow: 0 3px 10px rgba(0,0,0,0.1);
}

.emplacement-code {
  display: block;
  font-weight: bold;
  color: #007bff;
  margin-bottom: 5px;
  font-size: 1rem;
}

.emplacement-type {
  font-size: 0.8rem;
  color: #6c757d;
}

/* Responsive Design */
@media (max-width: 768px) {
  .identity-card {
    flex-direction: column;
    padding: 20px;
    gap: 20px;
  }
  
  .photo-container {
    width: 120px;
    height: 120px;
    margin: 0 auto;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .card-header {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }
  
  .card-header h2 {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .identity-card {
    padding: 15px;
  }
  
  .photo-container {
    width: 100px;
    height: 100px;
  }
  
  .no-photo {
    font-size: 3rem;
  }
  
  .card-header h2 {
    font-size: 1.3rem;
  }
  
  .emplacements-grid {
    grid-template-columns: 1fr;
  }
}
</style>
